import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { PetForm } from 'components/Pet/Form/PetForm';
import { PetOption } from 'components/Pet/Option/PetOption';
import { Alert } from 'components/UI/Alert/Alert';
import { Button } from 'components/UI/Button/Button';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { dispatch } from 'store';
import { Pet } from 'types/Pet';
import { navigateToStep } from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    saveOnlineBookingPetThunk,
    setOnlineBookingSelectedPets
} from 'actions/onlineBooking/actions/OnlineBookingPetActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { arrivedFromPortal, goToPetParentPortal } from 'utils/petParentPortal';
import { getVaccineIssues, requiredVaccines } from 'utils/petUtils';
import classes from './OnlineBookingPetListStep.module.scss';

export const OnlineBookingPetListStep: React.FC = () => {
    const marketplace = useMarketplace();

    const { customer, isPending, selectedPets, isReturningClient } = useOnlineBookingState();

    const [editingPetUuid, setEditingPetUuid] = React.useState<string | null>(null);
    const [selectedUuids, setSelectedUuids] = React.useState<Array<string>>(
        selectedPets.map(pet => pet.uuid) || []
    );

    const somePetHasInvalidVaccines = React.useMemo(() => {
        if (!marketplace.settings.requireVaccinesEnabled) {
            return false;
        }

        const selectedPets = customer.pets.filter(pet => selectedUuids.includes(pet.uuid));

        return selectedPets.some(pet => {
            const issues = getVaccineIssues(
                pet.vaccines,
                requiredVaccines(marketplace, pet.type.id)
            );

            return issues.missing.length > 0 || issues.expired.length > 0;
        });
    }, [selectedUuids, customer.pets, marketplace]);

    const isValid = React.useMemo(() => {
        if (isPending) {
            return false;
        }

        if (somePetHasInvalidVaccines) {
            return false;
        }

        return selectedUuids.length > 0;
    }, [selectedUuids, isPending, somePetHasInvalidVaccines]);

    const petChangeHandler = (petUuid: string, checked: boolean) => {
        if (checked) {
            setSelectedUuids([...selectedUuids, petUuid]);
        } else {
            setSelectedUuids(selectedUuids.filter(uuid => uuid !== petUuid));
        }
    };

    const addPetHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.CreatePet));
    };

    const editPetHandler = (uuid: string) => {
        setEditingPetUuid(uuid);
    };

    const editedPetHandler = async (pet: Pet) => {
        try {
            await dispatch(saveOnlineBookingPetThunk(pet));

            setEditingPetUuid(null);
        } catch {
            // Keep the edit form open so the customer can act on the error
            // (a vaccine record failure opens its modal on top of it).
        }
    };

    const backHandler = React.useCallback(() => {
        if (arrivedFromPortal()) {
            goToPetParentPortal(marketplace.slug);
            return;
        }

        dispatch(navigateToStep(OnlineBookingStep.Signup));
    }, [marketplace.slug]);

    const submitHandler = () => {
        const selectedPets = customer.pets.filter(pet => selectedUuids.includes(pet.uuid));

        dispatch(setOnlineBookingSelectedPets(selectedPets));
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    };

    return (
        <TwoColumnsLayout
            header={
                <OnlineBookingHeader
                    onBack={isReturningClient && !arrivedFromPortal() ? undefined : backHandler}
                />
            }
        >
            <LeftColumn>
                <>
                    <h1 className="h2 underlined w-full">
                        {isReturningClient ? `Welcome back ${customer?.name}` : 'Select your pets'}
                    </h1>

                    {somePetHasInvalidVaccines && (
                        <Alert variant="warning" icon={faInfoCircle} className="mt-4 mb-1">
                            Add or update the required vaccines before booking.
                        </Alert>
                    )}

                    {customer.pets.map(pet => (
                        <div key={pet.uuid} className="w-full">
                            {editingPetUuid === pet.uuid ? (
                                <PetForm
                                    pet={pet}
                                    showSubmitButton
                                    enforceRequirements
                                    loading={isPending}
                                    className={classes.editForm}
                                    submitButtonLabel="Update"
                                    onSubmit={editedPetHandler}
                                    onCancel={() => setEditingPetUuid(null)}
                                />
                            ) : (
                                <PetOption
                                    pet={pet}
                                    enforceRequirements
                                    onEdit={() => editPetHandler(pet.uuid)}
                                    checked={selectedUuids.includes(pet.uuid)}
                                    onChange={checked => petChangeHandler(pet.uuid, checked)}
                                />
                            )}
                        </div>
                    ))}

                    <Button variant="link" className={classes.addPetButton} onClick={addPetHandler}>
                        <FontAwesomeIcon icon={faPlus} />

                        <span>Add new pet</span>
                    </Button>

                    <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                        <PoweredByCuddles />
                    </div>
                </>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={<OnlineBookingFooter disabled={!isValid} onSubmit={submitHandler} />}
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
